'use client';

import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { Card } from '@/components/common/Card';
import { FormInput } from '@/components/forms/FormInput';
import { BudgetInput } from '@/components/forms/BudgetInput';
import { Button } from '@/components/common/Button';
import { Alert } from '@/components/common/Alert';
import { FormSelect } from '@/components/forms/FormSelect';
import { EmptyState } from '@/components/common/EmptyState';
import { Edit } from 'lucide-react';
import { EditAuthGuard } from '@/components/common/EditAuthGuard';

import { denominationMultipliers } from '@/lib/utils/money';
import type { PlayerRole, Tournament, Pool } from '@/lib/types/auction';
import { formatCurrency } from '@/lib/utils/format';
import { Users, Wallet, Plus, Database } from 'lucide-react';

/** Validation Schema */
const poolFormSchema = z.object({
  name: z.string().min(3, 'Pool name must be at least 3 characters'),
  budgetAmount: z.coerce
    .number({ invalid_type_error: 'Base price is required' })
    .min(1, 'Base price must be greater than 0'),
  budgetDenom: z.enum(['thousands', 'lacs', 'crores']),
  playerRole: z.enum(['player', 'icon']),
});

type PoolFormData = z.infer<typeof poolFormSchema>;

interface PoolSetupProps {
  showCreatedPool: boolean;
  showCreatePool: boolean;
  tournament?: Tournament;
  editingPool?: Pool | null;
  onSubmit: (data: {
    name: string;
    basePrice: number;
    playerRole: PlayerRole;
  }) => Promise<Pool>;
  onEditPool?: (pool: Pool | null) => void;
}

export function PoolSetup({
  tournament,
  showCreatedPool,
  showCreatePool,
  editingPool,
  onSubmit,
  onEditPool,
}: PoolSetupProps) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<PoolFormData>({
    resolver: zodResolver(poolFormSchema),
    defaultValues: editingPool
      ? {
          name: editingPool.name,
          budgetAmount: getBudgetAmount(editingPool.basePrice),
          budgetDenom: getBudgetDenom(editingPool.basePrice),
          playerRole: editingPool.playerRole,
        }
      : {
          name: '',
          budgetAmount: 1,
          budgetDenom: 'thousands',
          playerRole: 'player',
        },
  });

  const [createdPools, setCreatedPools] = useState<Pool[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (editingPool) {
      reset({
        name: editingPool.name,
        budgetAmount: getBudgetAmount(editingPool.basePrice),
        budgetDenom: getBudgetDenom(editingPool.basePrice),
        playerRole: editingPool.playerRole,
      });
    } else {
      reset({
        name: '',
        budgetAmount: 1,
        budgetDenom: 'thousands',
        playerRole: 'player',
      });
    }
  }, [editingPool, reset]);

  useEffect(() => {
    if (tournament?.pools) {
      setCreatedPools(tournament.pools);
    }
  }, [tournament?.pools]);

  const submitPool = async (data: PoolFormData) => {
    setError(null);
    try {
      const computedBasePrice =
        data.budgetAmount * denominationMultipliers[data.budgetDenom];

      const newPool = await onSubmit({
        name: data.name,
        basePrice: computedBasePrice,
        playerRole: data.playerRole as PlayerRole,
      });

      if (editingPool) {
        setCreatedPools((prev) =>
          prev.map((p) => (p.id === editingPool.id ? newPool : p))
        );
      } else {
        setCreatedPools((prev) => [...prev, newPool]);
      }

      onEditPool?.(null);

      if (!editingPool) {
        reset();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Pool operation failed');
    }
  };

  const gridClasses =
    showCreatedPool && showCreatePool
      ? 'grid grid-cols-1 sm:grid-cols-2 gap-6'
      : 'grid grid-cols-1';

  return (
    <div className="space-y-2 sm:space-y-4">
      {error && <Alert type="error" message={error} />}

      <div className={gridClasses}>
        {showCreatedPool && (
          <Card className="flex max-h-[800px] min-h-[400px] flex-col overflow-auto rounded-none border-none hover:translate-y-0">
            <div className="p-2 sm:p-3">
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  Created Pools
                </h3>
                <span className="rounded-full bg-primary-50 px-3 py-1 text-sm font-medium text-primary-600">
                  {createdPools.length}{' '}
                  {createdPools.length === 1 ? 'pool' : 'pools'}
                </span>
              </div>

              {createdPools.length > 0 ? (
                <div className="space-y-4">
                  {createdPools.map((pool) => (
                    <div
                      key={pool.id}
                      className="group relative rounded-lg border border-gray-100/50 bg-gradient-to-r from-gray-50/80 to-gray-100/80 p-4 transition-all duration-300 hover:shadow-md"
                    >
                      <div className="flex flex-col space-y-4">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium text-gray-900">
                            {pool.name}
                          </h4>
                          <div className="flex items-center gap-2">
                            <span className="rounded-full bg-accent-blue/10 px-2 py-0.5 text-xs font-medium capitalize text-accent-blue">
                              {pool.playerRole}
                            </span>
                            <EditAuthGuard
                              tournament={tournament!}
                              entity="pool"
                            >
                              <Button
                                variant="light"
                                size="sm"
                                onClick={() => onEditPool?.(pool)}
                                leftIcon={<Edit className="h-4 w-4" />}
                              >
                                Edit
                              </Button>
                            </EditAuthGuard>
                          </div>
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <Users className="h-4 w-4 text-primary-600" />
                            <span className="text-sm text-gray-700">
                              {pool.players?.length || 0} players
                            </span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Wallet className="h-4 w-4 text-accent-green" />
                            <span className="text-sm font-medium text-gray-900">
                              {formatCurrency(pool.basePrice)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState
                  title="Pool Setup"
                  message={
                    <div className="space-y-3 text-left">
                      <p className="mb-4 text-sm text-gray-600">
                        Create player pools to organize participants by role or
                        skill level.
                      </p>
                      <div className="space-y-2">
                        <div className="flex items-start gap-2">
                          <div className="mt-0.5 flex h-4 w-4 items-center justify-center">
                            <div className="h-1.5 w-1.5 rounded-full bg-primary-500" />
                          </div>
                          <p className="text-xs leading-relaxed text-gray-600">
                            Pools help categorize players (e.g., star players,
                            rookie, children, etc)
                          </p>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="mt-0.5 flex h-4 w-4 items-center justify-center">
                            <div className="h-1.5 w-1.5 rounded-full bg-primary-500" />
                          </div>
                          <p className="text-xs leading-relaxed text-gray-600">
                            Base price sets the minimum bid amount for players
                            in this pool
                          </p>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="mt-0.5 flex h-4 w-4 items-center justify-center">
                            <div className="h-1.5 w-1.5 rounded-full bg-primary-500" />
                          </div>
                          <p className="text-xs leading-relaxed text-gray-600">
                            Create multiple pools to organize players
                            effectively, if needed
                          </p>
                        </div>
                      </div>
                    </div>
                  }
                  icon={<Database className="h-12 w-12" />}
                />
              )}
            </div>
          </Card>
        )}

        {(showCreatePool || editingPool) && (
          <EditAuthGuard tournament={tournament!} entity="pool">
            <Card className="flex max-h-[800px] min-h-[400px] flex-col overflow-auto rounded-none border-none hover:translate-y-0">
              <div className="p-2 sm:p-3">
                <div className="mb-2 flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {editingPool ? 'Edit Pool' : 'Create New Pool'}
                  </h3>
                  <span className="flex items-center rounded-full bg-gradient-to-r from-primary-500 to-accent-blue px-3 py-1 text-xs font-medium text-white">
                    <Database className="mr-1 h-3 w-3" />
                    {editingPool ? 'Edit Pool' : 'New Pool'}
                  </span>
                </div>

                {/* Show status warning if tournament is not in editable state */}
                {tournament &&
                  !['DRAFT', 'REGISTRATION'].includes(tournament.status) && (
                    <Alert
                      type="warning"
                      message={`Pool editing is not allowed when tournament status is ${tournament.status}. Only DRAFT and REGISTRATION phases allow pool modifications.`}
                      className="mb-4"
                    />
                  )}

                <form onSubmit={handleSubmit(submitPool)} className="space-y-4">
                  <FormInput
                    name="name"
                    label="Pool Name"
                    register={register}
                    error={errors.name?.message}
                    placeholder="Enter pool name"
                  />

                  <BudgetInput
                    amountName="budgetAmount"
                    denomName="budgetDenom"
                    register={register}
                    amountError={errors.budgetAmount?.message}
                    denomError={errors.budgetDenom?.message}
                  />

                  <FormSelect
                    name="playerRole"
                    label="Player Role"
                    register={register}
                    error={errors.playerRole?.message}
                    options={[{ value: 'player', label: 'Player' }]}
                  />

                  <div className="flex gap-2">
                    {editingPool && (
                      <Button
                        variant="light"
                        onClick={() => onEditPool?.(null)}
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                    )}
                    <Button
                      type="submit"
                      isLoading={isSubmitting}
                      className="flex-1 bg-gradient-to-r from-primary-500 to-accent-blue"
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      {editingPool ? 'Save Changes' : 'Add Pool'}
                    </Button>
                  </div>
                </form>
              </div>
            </Card>
          </EditAuthGuard>
        )}
      </div>
    </div>
  );
}

function getBudgetAmount(basePrice: number): number {
  if (basePrice >= denominationMultipliers.crores) {
    return basePrice / denominationMultipliers.crores;
  }
  if (basePrice >= denominationMultipliers.lacs) {
    return basePrice / denominationMultipliers.lacs;
  }
  return basePrice / denominationMultipliers.thousands;
}

function getBudgetDenom(basePrice: number): 'thousands' | 'lacs' | 'crores' {
  if (basePrice >= denominationMultipliers.crores) {
    return 'crores';
  }
  if (basePrice >= denominationMultipliers.lacs) {
    return 'lacs';
  }
  return 'thousands';
}
